import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import useGetListClientsByAppInstance from 'redux/slices/appInstance/hooks/useGetListClientsByAppInstance';
import { Box, Tooltip, Typography, useMediaQuery } from '@mui/material';
import useModal from '@tenant/utility/hooks/useModal';
import IdpClientModalForm from './IdpClientModalForm';
import IdpClientEnum from 'constants/IdpClientEnum';
import DeleteDialog from '@tenant/core/App/Dialog/DeleteDialog';
import useDeleteClientByAppInstance from 'redux/slices/appInstance/hooks/useDeleteClientByAppInstance';
import useUpdateClientByAppInstance from 'redux/slices/appInstance/hooks/useUpdateClientByAppInstance';
import { useTheme } from '@emotion/react';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import AppExpandResult from '@tenant/core/App/AppExpandResult';

const IdpClientTable = ({ appInstanceId, newClients = [] }) => {
	const canEdit = useAllowedAccess([AuthorizePermission.APP_INSTANCE_CONTAINER_IDP_CLIENT_UPDATE]);
	const canDelete = useAllowedAccess([AuthorizePermission.APP_INSTANCE_CONTAINER_IDP_CLIENT_DELETE]);
	const getListQuery = useGetListClientsByAppInstance(appInstanceId);
	const deleteClientMutate = useDeleteClientByAppInstance();
	const updateClientMutate = useUpdateClientByAppInstance();
	const [selected, setSelected] = useState();
	const theme = useTheme();
	const isFlex = useMediaQuery(theme.breakpoints.up('xl'));
	const { visible, onShow, onClose } = useModal();
	const { visible: visibleDelete, onShow: onShowDelete, onClose: onCloseDelete } = useModal();

	const handleShow = item => {
		setSelected(item);
		onShow();
	};

	const handleClose = () => {
		setSelected();
		onClose();
	};

	const handleShowDelete = item => {
		setSelected(item);
		onShowDelete();
	};

	const handleCloseDelete = () => {
		setSelected();
		onCloseDelete();
	};

	const handleUpdate = data => {
		updateClientMutate.mutate(
			{
				...data,
				appInstanceId,
				idpClientId: selected.Id
			},
			{
				onSuccess() {
					handleClose();
					getListQuery.customRefetch();
				}
			}
		);
	};

	const handleSaveDelete = () => {
		deleteClientMutate.mutate(
			{
				appInstanceId,
				idpClientId: selected.Id
			},
			{
				onSuccess() {
					handleCloseDelete();
					getListQuery.customRefetch();
				}
			}
		);
	};

	const columns = [
		{
			field: 'ClientName',
			headerName: 'Client ID',
			width: 300,
			sortable: true,
			flex: isFlex ? 1 : undefined,
			renderCell: params => (
				<Tooltip title={params.value}>
					<Typography
						variant="body2"
						sx={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap'
						}}
					>
						{params.value}
					</Typography>
				</Tooltip>
			)
		},
		{
			field: 'AuthenticationFlow',
			headerName: 'Flow',
			width: 175,
			sortable: true,
			renderCell: params => IdpClientEnum.find(e => e.Value === params.value)?.Description
		},
		{
			field: 'RedirectUris',
			headerName: 'Redirect Uri',
			width: 300,
			sortable: true,
			renderCell: params => <AppExpandResult items={params.value} maxVisibleItems={1} />
		},
		{
			field: 'LogoutRedirectUris',
			headerName: 'Logout Redirect Uri',
			width: 300,
			sortable: true,
			renderCell: params => <AppExpandResult items={params.value} maxVisibleItems={1} />
		},
		{
			field: 'Action',
			headerName: '',
			width: 50,
			type: 'actions',
			getActions: params => {
				const actions = [];

				if (canEdit) {
					actions.push(<GridActionsCellItem sx={{ minWidth: 150 }} key="app-request-action--edit" label="Edit" showInMenu onClick={() => handleShow(params.row)} />);
				}

				if (canDelete) {
					actions.push(<GridActionsCellItem key="app-request-action--delete" label="Delete" showInMenu onClick={() => handleShowDelete(params.row)} />);
				}

				return actions;
			}
		}
	].filter(e => canEdit || canDelete || e.field !== 'Action');

	const rows = getListQuery?.data?.data ? [...newClients, ...(getListQuery?.data?.data ?? [])] : [...newClients];

	return (
		<>
			<IdpClientModalForm
				isEdit
				visible={visible}
				loading={updateClientMutate.isPending}
				handleClose={handleClose}
				handleSubmit={handleUpdate}
				initialValues={
					selected
						? {
								...selected,
								RedirectUri: selected.RedirectUris.join('\n'),
								LogoutRedirectUri: selected.LogoutRedirectUris.join('\n'),
								AuthenticationFlow: IdpClientEnum.find(e => e.Value === selected.AuthenticationFlow),
								IdpClientId: selected.Id
						  }
						: undefined
				}
			/>
			<DeleteDialog visible={visibleDelete} loading={deleteClientMutate.isPending} handleClose={handleCloseDelete} handleSave={handleSaveDelete} />{' '}
			<DataGrid
				autoHeight
				hideFooter
				disableColumnSelector
				disableColumnMenu
				loading={getListQuery.isPending || getListQuery.isRefetching}
				rows={rows}
				columns={columns}
				getRowId={row => row.ClientId}
				getRowHeight={({ model }) => {
					const redirectUrisLines = model.RedirectUris?.length || 0;
					const logoutRedirectUrisLines = model.LogoutRedirectUris?.length || 0;
					const maxLines = Math.max(redirectUrisLines, logoutRedirectUrisLines, 1);
					return Math.max(52, maxLines * 24 + 16);
				}}
			/>
		</>
	);
};

export default React.memo(IdpClientTable);

IdpClientTable.propTypes = {
	appInstanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	newClients: PropTypes.array
};
